// projectWizard/steps/GenericStep.jsx
//
// Renders any step from the model by mapping each field's declared type to
// the matching shared control. Steps 1 and 2 have bespoke components with
// hand-written copy; every other step is driven from here, which means all
// 66 fields are editable and consistently styled from day one rather than
// waiting for nine hand-built screens. A bespoke step can replace this at
// any time with no change to the wizard or the data contract.

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  SectionCard, TextInput, DateInput, NumberInput, ChipsInput, ToggleCard,
  AssetInput, CardSelectGroup, AdvancedPanel, BRAND,
} from '../WizardControls';
import { FIELD_TYPES, FIELD_LABELS, DELIVERABLE_OPTIONS, CERTIFICATE_OPTIONS, toList } from '../projectFormModel';

const humanise = (field) =>
  FIELD_LABELS[field] ||
  field.replace(/_/g, ' ').replace(/\burl\b/i, 'URL').replace(/^./, (c) => c.toUpperCase());

const PERCENT_FIELDS = new Set(['passing_score', 'min_ai_review_score', 'code_coverage_requirement']);
const LONG_TEXT_FIELDS = new Set([
  'learning_objectives', 'expected_outcomes', 'description', 'vision', 'project_summary',
  'problem_statement', 'business_value', 'student_benefits', 'career_benefits',
]);
const ASSET_FIELDS = new Set([
  'logo_url', 'thumbnail_url', 'banner_url', 'architecture_diagram_url',
  'demo_video_url', 'documentation_url', 'community_link', 'repository_url',
]);

/** One field -> one control, chosen from the declared type in the model. */
const renderField = (field, values, errors, setField, onUpload) => {
  const id = 'pf-' + field.replace(/_/g, '-');
  const label = humanise(field);
  const value = values[field];
  const error = errors[field];
  const type = FIELD_TYPES[field];

  if (ASSET_FIELDS.has(field)) {
    return (
      <AssetInput
        key={field} id={id} label={label} value={value} error={error}
        onChange={(v) => setField(field, v)}
        onUpload={field === 'repository_url' || field === 'community_link' ? undefined : onUpload}
        accept={field === 'demo_video_url' ? 'video/*' : field === 'documentation_url' ? '.pdf,.md,.doc,.docx' : 'image/*'}
      />
    );
  }

  switch (type) {
    case 'list':
      return (
        <ChipsInput
          key={field} id={id} label={label} value={toList(value)} error={error}
          onChange={(v) => setField(field, v)}
          hint="Type a value and press Enter."
        />
      );
    case 'date':
      return (
        <DateInput
          key={field} id={id} label={label} value={value} error={error}
          onChange={(v) => setField(field, v)}
        />
      );
    case 'number':
      return (
        <NumberInput
          key={field} id={id} label={label} value={value} error={error}
          onChange={(v) => setField(field, v)}
          min={0}
          max={PERCENT_FIELDS.has(field) ? 100 : undefined}
          suffix={PERCENT_FIELDS.has(field) ? '%' : undefined}
        />
      );
    case 'bool':
      return (
        <Box key={field} sx={{ mb: 2 }}>
          <ToggleCard
            id={id} label={label} checked={value === true}
            onChange={(v) => setField(field, v)}
          />
        </Box>
      );
    default:
      return (
        <TextInput
          key={field} id={id} label={label} value={value} error={error}
          onChange={(v) => setField(field, v)}
          multiline={LONG_TEXT_FIELDS.has(field)}
          minRows={LONG_TEXT_FIELDS.has(field) ? 4 : undefined}
        />
      );
  }
};

const GenericStep = ({ step, values, errors = {}, setField, onUpload }) => {
  // Deliverables get card selection rather than a checkbox list.
  if (step.id === 'deliverables') {
    const selected = toList(values.deliverables);
    const toggle = (v) =>
      setField('deliverables', selected.indexOf(v) === -1 ? selected.concat(v) : selected.filter((x) => x !== v));
    return (
      <SectionCard title={step.label} description={step.description}>
        <CardSelectGroup
          label="Required deliverables"
          hint="Select everything a team must submit to be considered complete."
          options={DELIVERABLE_OPTIONS}
          selected={selected}
          onToggle={toggle}
          error={errors.deliverables}
        />
        {selected.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: BRAND.muted, mt: 1 }}>
            Nothing selected yet — teams will not be asked to submit anything.
          </Typography>
        ) : null}
      </SectionCard>
    );
  }

  // Certificates are four booleans — shown as toggle cards, not checkboxes.
  if (step.id === 'certificates') {
    return (
      <SectionCard title={step.label} description={step.description}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {CERTIFICATE_OPTIONS.map((opt) => (
            <ToggleCard
              key={opt.field}
              id={'pf-' + opt.field.replace(/_/g, '-')}
              label={opt.label}
              hint={opt.hint}
              checked={values[opt.field] === true}
              onChange={(v) => setField(opt.field, v)}
            />
          ))}
        </Box>
      </SectionCard>
    );
  }

  const advanced = Array.isArray(step.advanced) ? step.advanced : [];

  return (
    <SectionCard title={step.label} description={step.description}>
      {step.fields.map((f) => renderField(f, values, errors, setField, onUpload))}
      {advanced.length ? (
        <AdvancedPanel count={advanced.length}>
          {advanced.map((f) => renderField(f, values, errors, setField, onUpload))}
        </AdvancedPanel>
      ) : null}
    </SectionCard>
  );
};

export default React.memo(GenericStep);
